export interface MusicianService {
  name: string;
  agency: "The Lost Strings" | "Pacific Currents Music";
  costRange: { min: number; max: number };
  startingPrice1Hr: number;
  price4Hr: number;
  travelFee: number;
  rollup1Hr: number;
  rollup4Hr: number;
}

export const PROFIT_MARGIN = 0.67;

export const musicianServices: MusicianService[] = [
  // The Lost Strings (Indian Specialists)
  {
    name: "Shehnai Players",
    agency: "The Lost Strings",
    costRange: { min: 700, max: 1000 },
    startingPrice1Hr: 1500,
    price4Hr: 2800,
    travelFee: 500,
    rollup1Hr: 2000,
    rollup4Hr: 3300,
  },
  {
    name: "Indian Classical Strings",
    agency: "The Lost Strings",
    costRange: { min: 700, max: 1000 },
    startingPrice1Hr: 1500,
    price4Hr: 2800,
    travelFee: 500,
    rollup1Hr: 2000,
    rollup4Hr: 3300,
  },
  {
    name: "Tabla Players",
    agency: "The Lost Strings",
    costRange: { min: 600, max: 900 },
    startingPrice1Hr: 1000,
    price4Hr: 1800,
    travelFee: 500,
    rollup1Hr: 1500,
    rollup4Hr: 2300,
  },
  {
    name: "Dhol Players",
    agency: "The Lost Strings",
    costRange: { min: 300, max: 500 },
    startingPrice1Hr: 500,
    price4Hr: 900,
    travelFee: 500,
    rollup1Hr: 1000,
    rollup4Hr: 1400,
  },
  // Pacific Currents Music (Western Musicians)
  {
    name: "Harpists",
    agency: "Pacific Currents Music",
    costRange: { min: 425, max: 675 },
    startingPrice1Hr: 1000,
    price4Hr: 1800,
    travelFee: 125,
    rollup1Hr: 1125,
    rollup4Hr: 1925,
  },
  {
    name: "Violinists",
    agency: "Pacific Currents Music",
    costRange: { min: 275, max: 525 },
    startingPrice1Hr: 800,
    price4Hr: 1500,
    travelFee: 125,
    rollup1Hr: 925,
    rollup4Hr: 1625,
  },
  {
    name: "Cellists",
    agency: "Pacific Currents Music",
    costRange: { min: 275, max: 525 },
    startingPrice1Hr: 800,
    price4Hr: 1500,
    travelFee: 125,
    rollup1Hr: 925,
    rollup4Hr: 1625,
  },
  {
    name: "Viola Players",
    agency: "Pacific Currents Music",
    costRange: { min: 275, max: 525 },
    startingPrice1Hr: 800,
    price4Hr: 1500,
    travelFee: 125,
    rollup1Hr: 925,
    rollup4Hr: 1625,
  },
  {
    name: "Saxophonists",
    agency: "Pacific Currents Music",
    costRange: { min: 250, max: 500 },
    startingPrice1Hr: 800,
    price4Hr: 1500,
    travelFee: 125,
    rollup1Hr: 925,
    rollup4Hr: 1625,
  },
  {
    name: "Pianists",
    agency: "Pacific Currents Music",
    costRange: { min: 250, max: 500 },
    startingPrice1Hr: 800,
    price4Hr: 1500,
    travelFee: 125,
    rollup1Hr: 925,
    rollup4Hr: 1625,
  },
  {
    name: "Drummers",
    agency: "Pacific Currents Music",
    costRange: { min: 250, max: 500 },
    startingPrice1Hr: 800,
    price4Hr: 1500,
    travelFee: 125,
    rollup1Hr: 925,
    rollup4Hr: 1625,
  },
  {
    name: "Guitarists",
    agency: "Pacific Currents Music",
    costRange: { min: 250, max: 500 },
    startingPrice1Hr: 800,
    price4Hr: 1500,
    travelFee: 125,
    rollup1Hr: 925,
    rollup4Hr: 1625,
  },
  {
    name: "Bassists",
    agency: "Pacific Currents Music",
    costRange: { min: 250, max: 500 },
    startingPrice1Hr: 800,
    price4Hr: 1500,
    travelFee: 125,
    rollup1Hr: 925,
    rollup4Hr: 1625,
  },
  {
    name: "Trumpet Players",
    agency: "Pacific Currents Music",
    costRange: { min: 250, max: 500 },
    startingPrice1Hr: 800,
    price4Hr: 1500,
    travelFee: 125,
    rollup1Hr: 925,
    rollup4Hr: 1625,
  },
  {
    name: "Trombonists",
    agency: "Pacific Currents Music",
    costRange: { min: 250, max: 500 },
    startingPrice1Hr: 800,
    price4Hr: 1500,
    travelFee: 125,
    rollup1Hr: 925,
    rollup4Hr: 1625,
  },
];

export interface EnsemblePackage {
  name: string;
  musicians: string[];
  moment: string;
  startingPrice: number;
  maxPrice: number;
  minimumPrice: number; // 67% margin maintained
  description: string;
}

// Helper to calculate minimum price at 67% margin
export const calculateMinimumPrice = (costs: number[]): number => {
  const totalCost = costs.reduce((sum, cost) => sum + cost, 0);
  return Math.ceil(totalCost / (1 - PROFIT_MARGIN));
};

// Pre-defined ensemble packages
export const ensemblePackages: EnsemblePackage[] = [
  {
    name: "Indian Ceremony Trio",
    musicians: ["Shehnai Players", "Tabla Players", "Indian Classical Strings"],
    moment: "Ceremony/Sacred Moments",
    startingPrice: 4000,
    maxPrice: 7400,
    minimumPrice: calculateMinimumPrice([1000, 900, 1000]),
    description: "Traditional shehnai, tabla, and strings for sacred ceremonies",
  },
  {
    name: "Cocktail Hour Strings Trio",
    musicians: ["Violinists", "Cellists", "Viola Players"],
    moment: "Cocktail Hour",
    startingPrice: 2400,
    maxPrice: 4500,
    minimumPrice: calculateMinimumPrice([525, 525, 525]),
    description: "Elegant strings ensemble for sophisticated cocktail ambiance",
  },
  {
    name: "Grand Entrance Package",
    musicians: ["Dhol Players", "Shehnai Players", "Tabla Players"],
    moment: "Grand Entrance/Baraat",
    startingPrice: 3000,
    maxPrice: 5500,
    minimumPrice: calculateMinimumPrice([500, 1000, 900]),
    description: "High-energy dhol, shehnai, and tabla for dramatic entrances",
  },
  {
    name: "Dinner Ambiance Duo",
    musicians: ["Harpists", "Violinists"],
    moment: "Dinner Service",
    startingPrice: 1800,
    maxPrice: 3300,
    minimumPrice: calculateMinimumPrice([675, 525]),
    description: "Refined harp and violin for elegant dining atmosphere",
  },
  {
    name: "Jazz Cocktail Hour",
    musicians: ["Pianists", "Saxophonists", "Bassists"],
    moment: "Cocktail Hour",
    startingPrice: 2400,
    maxPrice: 4500,
    minimumPrice: calculateMinimumPrice([500, 500, 500]),
    description: "Jazz trio for upscale cocktail hour vibes",
  },
  {
    name: "Reception Live Band",
    musicians: ["Guitarists", "Drummers", "Bassists", "Saxophonists"],
    moment: "Reception/Dancing",
    startingPrice: 3200,
    maxPrice: 6000,
    minimumPrice: calculateMinimumPrice([500, 500, 500, 500]),
    description: "Full band for live reception entertainment and dancing",
  },
];
